// Sound manager for momentum feedback
// All sounds are opt-in and respect system preferences
// Works across Chrome, Edge, Safari (desktop & iOS), Firefox

class SoundManager {
  constructor() {
    this.enabled = false;
    this.volume = 0.3; // Low volume by default
    this.audioContext = null;
  }

  // Initialize AudioContext (must be called after user interaction)
  // Supports both standard AudioContext and Safari's webkitAudioContext
  init() {
    try {
      if (!this.audioContext) {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (AudioContextClass) {
          this.audioContext = new AudioContextClass();
        }
      }
      // Resume if suspended (required for Safari/iOS after user gesture)
      if (this.audioContext?.state === 'suspended') {
        this.audioContext.resume();
      }
    } catch (e) {
      console.warn('Audio initialization failed:', e.message);
      this.enabled = false;
      this.audioContext = null;
    }
    return this.audioContext !== null;
  }

  // Check if sounds should play
  canPlay() {
    // Ensure AudioContext is initialized and running
    if (this.enabled && this.audioContext) {
      // Try to resume if suspended (Safari may suspend between interactions)
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume().catch(() => {});
      }
      return this.audioContext.state === 'running' || this.audioContext.state === 'suspended';
    }
    return false;
  }

  // Enable/disable sounds
  setEnabled(enabled) {
    this.enabled = enabled;
    try {
      if (enabled && this.audioContext?.state === 'suspended') {
        this.audioContext.resume();
      }
    } catch (e) {
      console.warn('Audio resume failed:', e.message);
    }
  }

  // Set volume (0-1)
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume));
  }

  // Play startup sound - soft electric hum
  playStartup() {
    if (!this.canPlay()) return;
    try {
      this.playTone({
        frequency: 220,
        duration: 0.4,
        type: 'sine',
        fadeIn: 0.1,
        fadeOut: 0.2,
        detune: 5,
      });
    } catch (e) {
      console.warn('Sound playback failed:', e.message);
    }
  }

  // Play progress sound - gentle rising rev (used for return to To Do)
  playProgress() {
    if (!this.canPlay()) return;
    try {
      this.playTone({
        frequency: 330,
        endFrequency: 440,
        duration: 0.25,
        type: 'sine',
        fadeIn: 0.05,
        fadeOut: 0.15,
        volumeMultiplier: 1.2, // 20% louder
      });
    } catch (e) {
      console.warn('Sound playback failed:', e.message);
    }
  }

  // Play completion sound - DISABLED, now using crackling pops in utils/sounds.js
  // Kept for API compatibility but does nothing
  playCompletion() {
    // Intentionally empty - completion sound is handled by playHeartbeatSound
  }

  // Play reset sound - gentle descending tone
  playReset() {
    if (!this.canPlay()) return;
    try {
      this.playTone({
        frequency: 392, // G4
        endFrequency: 330, // E4
        duration: 0.5,
        type: 'sine',
        fadeIn: 0.05,
        fadeOut: 0.35,
      });
    } catch (e) {
      console.warn('Sound playback failed:', e.message);
    }
  }

  // Play wave swoosh - musical swoosh for moving to In Progress
  // Works across all browsers including Safari/iOS
  playWaveSwoosh() {
    // Always try to play (similar to how completion sound works)
    // This ensures sound plays even if SoundManager state is not yet synced
    try {
      // Create or reuse AudioContext
      if (!this.audioContext) {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (AudioContextClass) {
          this.audioContext = new AudioContextClass();
        }
      }
      
      if (!this.audioContext) return;
      
      // Resume if suspended (required for Safari/iOS)
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume();
      }
      
      const now = this.audioContext.currentTime;
      const vol = (this.volume || 0.3) * 0.8; // 20% quieter
      
      // Musical tone layer - gentle ascending sweep
      const tone = this.audioContext.createOscillator();
      const toneGain = this.audioContext.createGain();
      tone.connect(toneGain);
      toneGain.connect(this.audioContext.destination);
      tone.frequency.setValueAtTime(280, now);
      tone.frequency.exponentialRampToValueAtTime(420, now + 0.25);
      tone.frequency.exponentialRampToValueAtTime(350, now + 0.5);
      tone.type = 'sine';
      
      toneGain.gain.setValueAtTime(0, now);
      toneGain.gain.linearRampToValueAtTime(vol * 0.4, now + 0.08);
      toneGain.gain.linearRampToValueAtTime(vol * 0.25, now + 0.3);
      toneGain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
      
      tone.start(now);
      tone.stop(now + 0.55);
      
      // Second harmonic for richness
      const tone2 = this.audioContext.createOscillator();
      const tone2Gain = this.audioContext.createGain();
      tone2.connect(tone2Gain);
      tone2Gain.connect(this.audioContext.destination);
      tone2.frequency.setValueAtTime(560, now);
      tone2.frequency.exponentialRampToValueAtTime(840, now + 0.25);
      tone2.frequency.exponentialRampToValueAtTime(700, now + 0.5);
      tone2.type = 'sine';
      
      tone2Gain.gain.setValueAtTime(0, now);
      tone2Gain.gain.linearRampToValueAtTime(vol * 0.15, now + 0.08);
      tone2Gain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
      
      tone2.start(now);
      tone2.stop(now + 0.45);
      
      // Soft air/swoosh layer (reduced, less breathy)
      const bufferSize = this.audioContext.sampleRate * 0.4;
      const buffer = this.audioContext.createBuffer(1, bufferSize, this.audioContext.sampleRate);
      const data = buffer.getChannelData(0);
      
      for (let i = 0; i < bufferSize; i++) {
        const t = i / bufferSize;
        const envelope = Math.sin(t * Math.PI) * Math.pow(1 - t, 0.6);
        data[i] = (Math.random() * 2 - 1) * envelope * 0.5;
      }
      
      const source = this.audioContext.createBufferSource();
      source.buffer = buffer;
      
      const filter = this.audioContext.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.setValueAtTime(600, now);
      filter.frequency.linearRampToValueAtTime(900, now + 0.2);
      filter.frequency.linearRampToValueAtTime(500, now + 0.4);
      filter.Q.value = 2;
      
      const noiseGain = this.audioContext.createGain();
      noiseGain.gain.setValueAtTime(0, now);
      noiseGain.gain.linearRampToValueAtTime(vol * 0.3, now + 0.1);
      noiseGain.gain.linearRampToValueAtTime(0, now + 0.4);
      
      source.connect(filter);
      filter.connect(noiseGain);
      noiseGain.connect(this.audioContext.destination);
      
      source.start(now);
    } catch (e) {
      console.warn('Wave sound playback failed:', e.message);
    }
  }

  // Core tone generator
  playTone({ 
    frequency, 
    endFrequency, 
    duration, 
    type = 'sine', 
    fadeIn = 0.05, 
    fadeOut = 0.1,
    detune = 0,
    volumeMultiplier = 1
  }) {
    if (!this.audioContext) return;

    try {
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      oscillator.type = type;
      oscillator.frequency.setValueAtTime(frequency, this.audioContext.currentTime);
      
      if (endFrequency) {
        oscillator.frequency.linearRampToValueAtTime(
          endFrequency, 
          this.audioContext.currentTime + duration
        );
      }

      if (detune) {
        oscillator.detune.setValueAtTime(detune, this.audioContext.currentTime);
      }

      // Volume envelope
      const now = this.audioContext.currentTime;
      const maxVolume = this.volume * 0.15 * volumeMultiplier; // Apply multiplier
      
      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(maxVolume, now + fadeIn);
      gainNode.gain.linearRampToValueAtTime(maxVolume, now + duration - fadeOut);
      gainNode.gain.linearRampToValueAtTime(0, now + duration);

      oscillator.start(now);
      oscillator.stop(now + duration);
    } catch (e) {
      console.warn('Tone playback failed:', e.message);
    }
  }
}

// Singleton instance
export const soundManager = new SoundManager();

// React hook for sound settings
import { useState, useEffect, useCallback } from 'react';
import { setSoundsEnabled as setUtilSoundsEnabled } from '@/utils/sounds';

export const useSoundSettings = () => {
  const [soundEnabled, setSoundEnabled] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('wwi_sounds');
      // Default to ON if not set
      if (stored === null) return true;
      return stored === 'true';
    }
    return true; // Default ON
  });

  // Initialize audio context on first load if sounds enabled
  useEffect(() => {
    if (soundEnabled) {
      soundManager.init();
    }
    soundManager.setEnabled(soundEnabled);
    // Mirror the toggle into the standalone utils/sounds.js module so its
    // Web-Audio sounds (heartbeat, lock-screen pin, focus swoosh, etc.)
    // honor the same "Momentum sounds" switch.
    setUtilSoundsEnabled(soundEnabled);
    localStorage.setItem('wwi_sounds', soundEnabled.toString());
  }, [soundEnabled]);

  const toggleSound = useCallback(() => {
    // Initialize audio context on first enable (requires user gesture)
    if (!soundEnabled) {
      soundManager.init();
    }
    setSoundEnabled(prev => !prev);
  }, [soundEnabled]);

  return {
    soundEnabled,
    setSoundEnabled,
    toggleSound,
    playStartup: () => soundManager.playStartup(),
    playProgress: () => soundManager.playProgress(),
    playCompletion: () => soundManager.playCompletion(),
    playReset: () => soundManager.playReset(),
    playWaveSwoosh: () => soundManager.playWaveSwoosh(),
  };
};

export default soundManager;
